import { useRef, useState } from 'react';

interface UploadSectionProps {
  onFileUpload: (file: File) => void;
  individuales: number;
  duplas: number;
  trios: number;
}

export default function UploadSection({ onFileUpload, individuales, duplas, trios }: UploadSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);

  const total = individuales + duplas + trios;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      onFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setFileName(file.name);
      onFileUpload(file);
    }
  };

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
          <i className="ri-upload-cloud-line text-xl text-white"></i>
        </div>
        <h2 className="text-xl font-bold text-gray-900">Subir Excel</h2>
      </div>

      <div
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer ${
          isDragging
            ? 'border-emerald-500 bg-emerald-50'
            : 'border-gray-300 hover:border-emerald-400 hover:bg-gray-50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full">
          <i className="ri-file-excel-2-line text-3xl text-emerald-600"></i>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {fileName || 'Arrastra tu archivo Excel aquí'}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          o haz clic para seleccionar un archivo
        </p>

        <button
          type="button"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-medium rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all shadow-sm whitespace-nowrap cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
        >
          <i className="ri-folder-open-line text-base"></i>
          Seleccionar Archivo
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-lg text-blue-600 mt-0.5"></i>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Formato requerido</h4>
            <p className="text-sm text-blue-700">
              El archivo Excel debe contener las siguientes columnas: <strong>Número</strong>, <strong>Jugador 1</strong>, <strong>Jugador 2</strong>, <strong>Jugador 3</strong>, <strong>Club</strong> y <strong>Categoría</strong>
            </p>
          </div>
        </div>
      </div>

      {total > 0 && (
        <div className="mt-5 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-checkbox-circle-fill text-xl text-emerald-600"></i>
            <span className="text-sm font-semibold text-emerald-800">
              Archivo cargado correctamente — {total} registro{total !== 1 ? 's' : ''} en total
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-indigo-200">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-user-line text-indigo-600"></i>
              </div>
              <span className="text-sm font-bold text-indigo-700">{individuales}</span>
              <span className="text-xs text-indigo-500">Individual{individuales !== 1 ? 'es' : ''}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-emerald-200">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-group-line text-emerald-600"></i>
              </div>
              <span className="text-sm font-bold text-emerald-700">{duplas}</span>
              <span className="text-xs text-emerald-500">Dupla{duplas !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-amber-200">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-team-line text-amber-600"></i>
              </div>
              <span className="text-sm font-bold text-amber-700">{trios}</span>
              <span className="text-xs text-amber-500">Trío{trios !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}